import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TipTapImage from '@tiptap/extension-image';
import TipTapLink from '@tiptap/extension-link';
import { useDropzone } from 'react-dropzone';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BlogEditor = ({ blog, onClose }) => {
  const [title, setTitle] = useState(blog?.title || '');
  const [slug, setSlug] = useState(blog?.slug || '');
  const [excerpt, setExcerpt] = useState(blog?.excerpt || '');
  const [category, setCategory] = useState(blog?.category || 'technology');
  const [authorName, setAuthorName] = useState(blog?.author_name || '');
  const [authorRole, setAuthorRole] = useState(blog?.author_role || '');
  const [readTime, setReadTime] = useState(blog?.read_time || '');
  const [featuredImage, setFeaturedImage] = useState(blog?.featured_image || '');
  const [status, setStatus] = useState(blog?.status || 'draft');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // SEO fields
  const [metaTitle, setMetaTitle] = useState(blog?.meta_title || '');
  const [metaDescription, setMetaDescription] = useState(blog?.meta_description || '');
  const [keywords, setKeywords] = useState(blog?.keywords?.join(', ') || '');
  const [ogImage, setOgImage] = useState(blog?.og_image || '');
  const [schemaType, setSchemaType] = useState(blog?.schema_type || 'Article');

  const editor = useEditor({
    extensions: [
      StarterKit,
      TipTapImage?.configure({ inline: true }),
      TipTapLink?.configure({ openOnClick: false })
    ],
    content: blog?.content?.blocks?.[0]?.content || '<p>Start writing your blog post...</p>',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[400px] p-4'
      }
    }
  });

  const generateSlug = (text) => {
    return text?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-')?.replace(/(^-|-$)/g, '');
  };

  useEffect(() => {
    if (!blog && title) {
      setSlug(generateSlug(title));
    }
  }, [title, blog]);

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase?.storage?.from('blog-images')?.upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase?.storage?.from('blog-images')?.getPublicUrl(fileName);

      setFeaturedImage(publicUrl);
    } catch (err) {
      console.error('Error uploading image:', err);
      alert('Failed to upload image: ' + err?.message);
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.gif'] },
    maxFiles: 1
  });

  const handleSave = async (publishNow = false) => {
    if (!title || !slug || !excerpt || !authorName) {
      alert('Please fill in all required fields (title, slug, excerpt, author name)');
      return;
    }

    try {
      setSaving(true);
      const htmlContent = editor?.getHTML();
      const contentBlocks = [{ type: 'html', content: htmlContent }];

      // Parse keywords from comma-separated string to array
      const keywordsArray = keywords
        ?.split(',')
        ?.map(k => k?.trim())
        ?.filter(k => k?.length > 0);

      const blogData = {
        title,
        slug,
        excerpt,
        content: { blocks: contentBlocks },
        featured_image: featuredImage,
        category,
        author_name: authorName,
        author_role: authorRole,
        read_time: readTime,
        status: publishNow ? 'published' : status,
        meta_title: metaTitle || title,
        meta_description: metaDescription || excerpt,
        keywords: keywordsArray,
        og_image: ogImage || featuredImage,
        schema_type: schemaType,
        ...(publishNow && { published_at: new Date()?.toISOString() })
      };

      if (blog?.id) {
        const { error: updateError } = await supabase?.from('blogs')?.update(blogData)?.eq('id', blog?.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase?.from('blogs')?.insert([blogData]);

        if (insertError) throw insertError;
      }

      alert(blog?.id ? 'Blog updated successfully!' : 'Blog created successfully!');
      onClose();
    } catch (err) {
      console.error('Error saving blog:', err);
      alert('Failed to save blog: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    { value: 'technology', label: 'Technology' },
    { value: 'fire-science', label: 'Fire Science' },
    { value: 'case-studies', label: 'Case Studies' },
    { value: 'industry-news', label: 'Industry News' },
    { value: 'best-practices', label: 'Best Practices' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            {blog ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {blog ? 'Update your blog post content' : 'Write and publish a new blog post'}
          </p>
        </div>
        <Button variant="outline" onClick={onClose} iconName="X">
          Close
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Title"
            required
            value={title}
            onChange={(e) => setTitle(e?.target?.value)}
            placeholder="Enter blog title"
          />
          <Input
            label="Slug (URL)"
            required
            value={slug}
            onChange={(e) => setSlug(e?.target?.value)}
            placeholder="blog-post-url"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Excerpt <span className="text-destructive">*</span>
          </label>
          <textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e?.target?.value)}
            placeholder="Brief description of the blog post"
            rows={3}
            className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {categories?.map((cat) => (
                <option key={cat?.value} value={cat?.value}>
                  {cat?.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Author Name"
            required
            value={authorName}
            onChange={(e) => setAuthorName(e?.target?.value)}
            placeholder="John Doe"
          />
          <Input
            label="Author Role"
            value={authorRole}
            onChange={(e) => setAuthorRole(e?.target?.value)}
            placeholder="Fire Investigation Expert"
          />
          <Input
            label="Read Time"
            value={readTime}
            onChange={(e) => setReadTime(e?.target?.value)}
            placeholder="5 min read"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Featured Image
          </label>
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-smooth ${
              isDragActive
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                <span className="text-sm text-muted-foreground">Uploading...</span>
              </div>
            ) : featuredImage ? (
              <div className="space-y-2">
                <img
                  src={featuredImage}
                  alt="Featured"
                  className="max-h-40 mx-auto rounded-lg"
                />
                <p className="text-sm text-muted-foreground">
                  Click or drag to replace image
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Icon name="Upload" size={32} className="mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Drag and drop an image, or click to select
                </p>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Content
          </label>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="bg-muted border-b border-border p-2 flex items-center gap-1 flex-wrap">
              <button
                onClick={() => editor?.chain()?.focus()?.toggleBold()?.run()}
                className={`p-2 rounded hover:bg-background transition-smooth ${
                  editor?.isActive('bold') ? 'bg-background' : ''
                }`}
                type="button"
              >
                <Icon name="Bold" size={16} />
              </button>
              <button
                onClick={() => editor?.chain()?.focus()?.toggleItalic()?.run()}
                className={`p-2 rounded hover:bg-background transition-smooth ${
                  editor?.isActive('italic') ? 'bg-background' : ''
                }`}
                type="button"
              >
                <Icon name="Italic" size={16} />
              </button>
              <button
                onClick={() => editor?.chain()?.focus()?.toggleHeading({ level: 2 })?.run()}
                className={`p-2 rounded hover:bg-background transition-smooth ${
                  editor?.isActive('heading', { level: 2 }) ? 'bg-background' : ''
                }`}
                type="button"
              >
                <Icon name="Heading2" size={16} />
              </button>
              <button
                onClick={() => editor?.chain()?.focus()?.toggleBulletList()?.run()}
                className={`p-2 rounded hover:bg-background transition-smooth ${
                  editor?.isActive('bulletList') ? 'bg-background' : ''
                }`}
                type="button"
              >
                <Icon name="List" size={16} />
              </button>
              <button
                onClick={() => editor?.chain()?.focus()?.toggleOrderedList()?.run()}
                className={`p-2 rounded hover:bg-background transition-smooth ${
                  editor?.isActive('orderedList') ? 'bg-background' : ''
                }`}
                type="button"
              >
                <Icon name="ListOrdered" size={16} />
              </button>
            </div>
            <div className="bg-background">
              <EditorContent editor={editor} />
            </div>
          </div>
        </div>

        {/* SEO Fields Section */}
        <div className="border-t border-border pt-6 mt-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="Search" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">SEO Settings</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Optimize your blog post for search engines and social media sharing
          </p>
          
          <div className="space-y-4">
            <Input
              label="Meta Title"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e?.target?.value)}
              placeholder={title || "Enter SEO title (defaults to blog title)"}
              helperText={`${metaTitle?.length || 0}/60 characters (recommended)`}
            />
            
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Meta Description
              </label>
              <textarea
                value={metaDescription}
                onChange={(e) => setMetaDescription(e?.target?.value)}
                placeholder={excerpt || "Enter SEO description (defaults to excerpt)"}
                rows={3}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {metaDescription?.length || 0}/160 characters (recommended)
              </p>
            </div>
            
            <Input
              label="Keywords/Tags"
              value={keywords}
              onChange={(e) => setKeywords(e?.target?.value)}
              placeholder="fire investigation, forensics, case study (comma-separated)"
              helperText="Separate keywords with commas"
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Open Graph Image URL"
                value={ogImage}
                onChange={(e) => setOgImage(e?.target?.value)}
                placeholder={featuredImage || "URL for social media preview (defaults to featured image)"}
                helperText="Image shown when shared on social media"
              />
              
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Schema Type
                </label>
                <select
                  value={schemaType}
                  onChange={(e) => setSchemaType(e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="Article">Article</option>
                  <option value="BlogPosting">Blog Posting</option>
                  <option value="NewsArticle">News Article</option>
                  <option value="TechArticle">Technical Article</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Schema.org structured data type
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSave(false)}
            loading={saving}
            disabled={saving}
          >
            Save as Draft
          </Button>
          <Button
            onClick={() => handleSave(true)}
            loading={saving}
            disabled={saving}
            iconName="Send"
            iconPosition="left"
          >
            Publish Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;